import './JobInfo.scss';

import {
  ActionSheetButton,
  IonActionSheet,
  IonBadge,
  IonButton,
  IonCol,
  IonContent,
  IonFab,
  IonFabButton,
  IonGrid,
  IonIcon,
  IonPage,
  IonProgressBar,
  IonRow,
} from '@ionic/react';
import { createNextState } from '@reduxjs/toolkit';
import classNames from 'classnames';
import { downloadOutline, menuOutline, paperPlane, print, trash } from 'ionicons/icons';
import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { DqText } from 'src/components/dq-text';
import JobPressureCard from 'src/components/job-details/JobPressureCard';
import JobTimeRangeCard from 'src/components/job-details/JobTimeRangeCard';
import TubeSheetCard from 'src/components/job-details/TubeSheetCard';
import JobDetailsPrintModal, {
  type JobDetailsPrintOptions,
} from 'src/components/job-details/information/print/JobDetailsPrint';
import { JobSummaryCard } from 'src/components/job-details/job-summary/JobSummaryCard';
import { Chip, ChipType } from 'src/components/reusable-components/Chip';
import DesktopDetailPageHeader from 'src/components/reusable-components/desktop-header/DesktopDetailPageHeader';
import MobileDetailPageHeader from 'src/components/reusable-components/mobile-detail-page-header/MobileDetailPageHeader';
import IdleVsHp from 'src/components/visualizations/IdleVsHp';
import PieHighPressure from 'src/components/visualizations/pieHighPressure';
import { PieSentinelSystem } from 'src/components/visualizations/pieSentinelSystem';
import { JobMessages } from 'src/intl/JobMessages';
import { type JobLogV2Data } from 'src/utils/logs/types';
import { TubesCleanedGraph } from '../../components/job-details/TubesCleanedGraph';
import { YesNoModal } from '../../components/reusable-components/yes-no-modal/YesNoModal';
import { ShareTargetModal, type ShareTarget } from '../../components/share-report-modal/ShareTargetModal';
import { GenericMessages } from '../../intl/GenericMessages';
import type { JobLogDisplayPermissions } from '../../models/jobLog';
import { ListOrganizationsRequest, OrganizationStatus } from '../../models/organization';
import { AppRouteConstants } from '../app-layout/AppRouteConstants';
import type { JobInfoJobLog, JobInfoTimeRange } from './types';
import { useCanPrint, useIsMobile } from 'src/utils/helpers/platformHelpers';
import { ToastType, useToast } from 'src/components/reusable-components/useToast';
import { FamilyOrgShareFormData } from '../../components/share-report-modal/FamilyOrgShareForm';
import GuestShareModal from '../../components/share-report-modal/GuestShareModal';
import { GuestShareFormData } from '../../components/share-report-modal/GuestShareForm';
import FamilyOrgShareModal from '../../components/share-report-modal/FamilyOrgShareModal';
import { canShare, usePolicy } from '../../models/policies';
import { filterOutExpiredEmailGuestShares, filterOutExpiredPermissions } from '../../utils/logs/logHelpers';
import { RangeSet } from '../../components/visualizations/utils';
import { getControllerOffRanges } from './util';

interface JobInfoProps {
  /** The job log info from the sentinel system. This may not exist in all circumstances (i.e. for job logs created offline). */
  info: JobInfoJobLog | undefined;
  /** The parsed job log data. May be undefined while it is being loaded. */
  data: JobLogV2Data | undefined;
  /** The status of the job. */
  status: 'complete' | 'in-progress' | 'live';
  /** The display name of the job. */
  description: string | undefined;
  /** The subheading to display for the job. */
  subheading: string | undefined;
  /** The site name of the job. */
  siteName: string | undefined;
  /** The model number of the heat exchanger associated with the job. */
  heatExchangerModelNumber: string | undefined;
  /** The name of the organization associated with the job log. */
  orgName: string | undefined;
  /** The time zone that the job log was created in. */
  timezone: string;
  /** Describes the progress of fetching all the job log information, expressed as a number from 0 to 1. If undefined, does not apply. */
  fetchProgress?: number;
  /** Indicates if info component should render in "limited" mode, i.e., no operations allowed. */
  isLimitedMode?: boolean;
  /** Called when the user presses the print button. */
  onPrint?: (values: JobDetailsPrintOptions) => void;
  /** Called when the user presses the complete job log button. */
  onComplete?: () => Promise<void>;
  /** Show pencil icon for editing job details */
  showUpdateButton: boolean;
  /** Called when the user downloads the log file. */
  onDownload?: () => Promise<void>;
  /** Called when the user confirms deletion of the job. */
  onDelete?: () => Promise<void>;
  /** Called when the user confirms the job share operation. */
  onFamilyOrgShare?: (options: FamilyOrgShareFormData, orgName: string | undefined) => Promise<void>;
  /** Called when the user confirms the job share operation. */
  onGuestShare?: (options: GuestShareFormData) => Promise<void>;
  /** Called when the user confirms the job unshare operation. */
  onUnshare?: (unsharedPrincipals: string[]) => Promise<{ names: string[] }>;
  /** Indicates if there were errors fetching/parsing the log data */
  hasErrors: boolean;
  /** Permissions indicating what tables and controls are displayed */
  displayPermissions: JobLogDisplayPermissions;
  timeRange: JobInfoTimeRange;
  onTimeRangeChange?: (next: JobInfoTimeRange) => void;
}

interface JobInfoActionSheetButton extends ActionSheetButton {
  key: string;
  icon: string;
  badgeCount?: string;
}

type CurrentModal = 'ShareTarget' | 'FamilyOrgShare' | 'GuestShare' | 'Unshare' | 'Complete';
const initialScreenState = {
  currentModal: undefined as CurrentModal | undefined,
  shareTarget: undefined as ShareTarget | undefined,
  orgFilter: {
    statuses: ['active'] as OrganizationStatus[],
    pageNumber: 0,
    pageSize: 1000,
  } as ListOrganizationsRequest,
  toast: {
    active: false,
    header: '',
    color: 'success' as 'success' | 'danger',
    handle: undefined as ReturnType<typeof setTimeout> | undefined,
  },
};

type ScreenState = typeof initialScreenState;

type ScreenAction =
  | { type: 'Share:Init' }
  | { type: 'Share:TargetSelected'; target: ShareTarget }
  | { type: 'Share:Success' }
  | { type: 'Share:Error' }
  | { type: 'Unshare:Init' }
  | { type: 'Unshare:Success' }
  | { type: 'Unshare:Error' }
  | { type: 'Complete:Init' }
  | { type: 'Complete:Success' }
  | { type: 'Complete:Error' }
  | { type: 'Modal:Closed'; modal: CurrentModal };

function screenReducer(state: ScreenState, action: ScreenAction): ScreenState {
  return createNextState(state, draft => {
    switch (action.type) {
      case 'Complete:Init':
        draft.currentModal = 'Complete';
        break;

      case 'Share:Init':
        draft.currentModal = 'ShareTarget';
        break;

      case 'Modal:Closed':
        if (draft.currentModal === action.modal) {
          draft.currentModal = undefined;
        }
        break;

      case 'Share:TargetSelected':
        draft.shareTarget = action.target;

        if (action.target === 'guest') {
          draft.currentModal = 'GuestShare';
        } else {
          draft.currentModal = 'FamilyOrgShare';
        }
        break;

      case 'Unshare:Init':
        draft.currentModal = 'Unshare';
        break;

      case 'Complete:Success':
      case 'Complete:Error':
      case 'Share:Success':
      case 'Share:Error':
      case 'Unshare:Success':
      case 'Unshare:Error':
        draft.currentModal = undefined;
        break;
    }
  });
}

const stubYesNoClose = () => {};

/** The view for the job info page. Performs no data fetching on its own. */
export const JobInfo: React.FC<JobInfoProps> = props => {
  const { onDelete, onFamilyOrgShare, onGuestShare, onUnshare, onDownload, showUpdateButton, onPrint, onComplete } =
    props;
  const perms = props.displayPermissions;
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const canPrint = useCanPrint();
  const canShareJobReport = usePolicy(canShare, props.info);
  const [printDialogOpen, setPrintDialogOpen] = useState<boolean>(false);
  const [deleteWarningOpen, setDeleteWarningOpen] = useState<boolean>(false);
  const [showActionSheet, setShowActionSheet] = useState<boolean>(false);
  const [screenState, dispatch] = useReducer(screenReducer, initialScreenState);
  const [present] = useToast();

  /**
   *  For Controller FW >= 2.3.18: Use power events to determine when the controller is off, use the inverse to calculate the active runtime
   *
   *  If no power events are found, then the active runtime is the total msElapsed of the job (backwards compatible with previous versions)
   */
  const controllerOffRanges = useMemo<RangeSet>(() => {
    const powerEvents = props.data?.powerEvents || [];
    const msElapsed = props.data?.msElapsed || 0;
    return getControllerOffRanges(powerEvents, msElapsed);
  }, [props.data]);
  const activeRunTime = controllerOffRanges.sumExcluded();

  /**
   * For Controller FW >= 2.3.18: Adjust time range from job msElapsed to calendar msElapsed to make sure all data is captured
   *
   * For earlier FW versions, since controllerOffRanges will be empty, logFileTimeRangeMin = timeRange.min (backwards compatible)
   */
  const logFileTimeRangeMin = useMemo(() => {
    let newMin = props.timeRange.min;
    const ranges = controllerOffRanges.getRanges();
    ranges.forEach(range => {
      if (range.start < newMin) {
        const rangeMs = range.end - range.start;
        newMin += rangeMs;
      } else {
        return;
      }
    });

    return newMin;
  }, [props.timeRange.min, controllerOffRanges]);

  const logFileTimeRangeMax = useMemo(() => {
    let newMax = props.timeRange.max;
    const ranges = controllerOffRanges.getRanges();
    ranges.forEach(range => {
      if (range.start < newMax) {
        const rangeMs = range.end - range.start;
        newMax += rangeMs;
      } else {
        return;
      }
    });

    return newMax;
  }, [props.timeRange.max, controllerOffRanges]);

  const raiseToast = useCallback(
    (type: ToastType, header: string) => {
      present({ message: header }, type).then(); // then() consumes Promise, eliminating lint warning
    },
    [present],
  );

  const handleDownload = useMemo(() => {
    if (!onDownload) return undefined;
    return async () => {
      try {
        await onDownload();
      } catch {
        // TODO: Display error
      }
    };
  }, [onDownload]);

  const handlePrint = useMemo(() => {
    if (!onPrint || !canPrint) return undefined;

    return (options: JobDetailsPrintOptions) => {
      try {
        setPrintDialogOpen(false);
        onPrint(options);
      } catch {
        // TODO: Display error
      }
    };
  }, [canPrint, onPrint]);

  const handleComplete = useMemo(() => {
    if (!onComplete) return undefined;
    return async (didConfirm: boolean) => {
      if (!didConfirm) {
        dispatch({ type: 'Modal:Closed', modal: 'Complete' });
        return;
      }

      try {
        await onComplete();
        dispatch({ type: 'Complete:Success' });
      } catch {
        dispatch({ type: 'Complete:Error' });
        // TODO: Display error
      }
    };
  }, [onComplete]);

  const handleFamilyOrgShare = useMemo(() => {
    if (!onFamilyOrgShare) return undefined;

    return async (values: FamilyOrgShareFormData, orgName: string | undefined) => {
      try {
        await onFamilyOrgShare(values, orgName);
        raiseToast('success', formatMessage(JobMessages.shareJobSuccessMessage, { orgName }));
        dispatch({ type: 'Share:Success' });
      } catch (e) {
        raiseToast('danger', formatMessage(JobMessages.shareFailureMessage));
        dispatch({ type: 'Share:Error' });
      }
    };
  }, [formatMessage, onFamilyOrgShare, raiseToast]);

  const handleGuestShare = useMemo(() => {
    if (!onGuestShare) return undefined;

    return async (values: GuestShareFormData) => {
      try {
        await onGuestShare(values);
        raiseToast('success', formatMessage(JobMessages.shareJobSuccessMessage, { orgName: values.emailAddress }));
        dispatch({ type: 'Share:Success' });
      } catch (e) {
        raiseToast('danger', formatMessage(JobMessages.shareFailureMessage));
        dispatch({ type: 'Share:Error' });
      }
    };
  }, [formatMessage, onGuestShare, raiseToast]);

  const handleUnshareReport = useMemo(() => {
    if (!onUnshare) return undefined;

    return async (unshared: string[]) => {
      if (unshared.length === 0) {
        dispatch({ type: 'Modal:Closed', modal: 'ShareTarget' });
        return;
      }

      try {
        const { names } = await onUnshare(unshared);
        raiseToast('success', formatMessage(JobMessages.unshareJobSuccessMessage, { orgNames: names.join(', ') }));
        dispatch({ type: 'Unshare:Success' });
      } catch (e) {
        raiseToast('danger', formatMessage(JobMessages.unshareFailureMessage));
        dispatch({ type: 'Unshare:Error' });
      }
    };
  }, [onUnshare, raiseToast, formatMessage]);

  const handleDelete = useMemo(() => {
    if (!onDelete) return undefined;

    return async (isYes: boolean) => {
      setDeleteWarningOpen(false);
      if (!isYes) return;

      try {
        await onDelete();
      } catch (e) {
        // TODO: Handle with message
      }
    };
  }, [onDelete]);

  const permissions = filterOutExpiredPermissions(props.info?.permissions);
  const emailGuestShares = filterOutExpiredEmailGuestShares(props.info?.emailGuestShares);
  const numberOfShares = emailGuestShares.length + permissions.length;

  // Job info action buttons
  const jobInfoActions = React.useMemo(() => {
    const buttons: JobInfoActionSheetButton[] = [];

    if (!!handleDownload) {
      buttons.push({
        key: 'download',
        text: formatMessage(JobMessages.downloadLog),
        handler: async () => {
          setShowActionSheet(false);
          await handleDownload();
        },
        icon: downloadOutline,
      });
    }

    if (!!handlePrint) {
      buttons.push({
        key: 'print',
        text: formatMessage(JobMessages.jobInfoPrintDialogHeader),
        handler: () => {
          setPrintDialogOpen(true);
        },
        icon: print,
      });
    }

    let badgeCount = undefined;
    if (numberOfShares > 0) {
      if (numberOfShares > 9) {
        badgeCount = '9+';
      } else {
        badgeCount = `${numberOfShares}`;
      }
    }
    if (!!handleFamilyOrgShare) {
      buttons.push({
        key: 'share',
        text: formatMessage(JobMessages.shareLog),
        handler: () => {
          dispatch({ type: 'Share:Init' });
          setShowActionSheet(false);
        },
        icon: paperPlane,
        badgeCount,
      });
    }

    if (!!handleDelete) {
      buttons.push({
        key: 'delete',
        text: formatMessage(JobMessages.deleteLog),
        handler: () => {
          setDeleteWarningOpen(true);
        },
        icon: trash,
      });
    }

    return buttons;
  }, [handleDownload, handlePrint, handleFamilyOrgShare, handleDelete, formatMessage, numberOfShares]);

  const statusLabel =
    props.status === 'live'
      ? formatMessage(JobMessages.jobLive)
      : props.status === 'in-progress'
        ? formatMessage(JobMessages.jobInProgress)
        : formatMessage(JobMessages.jobComplete);

  const chipType =
    props.status === 'live'
      ? ChipType.JOB_LIVE
      : props.status === 'in-progress'
        ? ChipType.JOB_INPROGRESS
        : ChipType.JOB_COMPLETE;

  const headerButtons = React.useMemo(() => {
    if (!handleComplete) return null;

    return (
      <IonButton onClick={() => dispatch({ type: 'Complete:Init' })} disabled={props.isLimitedMode} fill="outline">
        {formatMessage(JobMessages.completeJobButtonLabel)}
      </IonButton>
    );
  }, [handleComplete, props.isLimitedMode, formatMessage]);

  return (
    <IonPage className="job-info-page">
      <Header
        isMobile={isMobile}
        title={formatMessage(JobMessages.jobDetails)}
        headerButtons={headerButtons}
        jobInfoProps={props}
        statusLabel={statusLabel}
        chipType={chipType}
        jobInfoActions={jobInfoActions}
      />
      <IonContent>
        <IonProgressBar
          value={props.fetchProgress}
          slot="fixed"
          className={classNames(
            'job-info-progress',
            (props.fetchProgress === 1 || !props.fetchProgress) && 'job-info-progress-hidden',
          )}
        />
        {isMobile && (
          <div className="ion-margin-bottom">
            <IonRow className="ion-flex ion-flex-direction-row ion-align-items-center ion-justify-content-between">
              <DqText variant="h1">{props.description || formatMessage(JobMessages.jobDetails)}</DqText>
              <Chip title={statusLabel} type={chipType} />
            </IonRow>
            {props.subheading && (
              <IonRow className="job-info-heading-row">
                <DqText>{props.subheading}</DqText>
              </IonRow>
            )}
          </div>
        )}

        {props.data && (
          <IonGrid className="job-info-card-grid">
            <IonRow>
              <IonCol size="12">
                <JobSummaryCard
                  info={props.info}
                  data={props.data}
                  description={props.description}
                  siteName={props.siteName}
                  heatExchangerModelNumber={props.heatExchangerModelNumber}
                  showTimeDetails={perms.showTimestamps}
                  showEstimatedTimeToCompletion={perms.showEstimatedTimeToCompletion}
                  showUpdateJobButton={showUpdateButton}
                  onReportSharedWithClicked={() => dispatch({ type: 'Share:Init' })}
                  jobStatus={props.status}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={props.timezone}
                  activeRunTime={activeRunTime}
                />
              </IonCol>
            </IonRow>

            {props.onTimeRangeChange && (
              <IonRow>
                <IonCol size="12">
                  <JobTimeRangeCard
                    activeRunTime={activeRunTime}
                    onChange={(min, max) => props.onTimeRangeChange?.({ min, max })}
                    showTimeDetails={perms.showTimestamps}
                  />
                </IonCol>
              </IonRow>
            )}

            {perms.showTubeGraph && (
              <>
                <IonRow>
                  <IonCol size="12">
                    <TubesCleanedGraph
                      tubeCount={props.data.tubeCount}
                      timeTable={props.data.timeTable}
                      timeRangeMin={props.timeRange.min}
                      timeRangeMax={props.timeRange.max}
                      timezone={props.timezone}
                      showAnnotations={perms.showAnnotations}
                      showTimestamps={perms.showTimestamps}
                      showAnnotationUser={perms.showAnnotationUser}
                      showAnnotationActions={perms.showAnnotationActions}
                      orgName={props.orgName}
                      controllerOffRanges={controllerOffRanges}
                    />
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="12">
                    <TubeSheetCard
                      data={props.data}
                      timeRangeMin={logFileTimeRangeMin}
                      timeRangeMax={logFileTimeRangeMax}
                      timezone={props.timezone}
                      showTimeDetails={perms.showTimestamps}
                    />
                  </IonCol>
                </IonRow>
              </>
            )}

            {perms.showPieCharts && (
              <IonRow>
                <IonCol size="12" sizeMd="6">
                  <PieSentinelSystem
                    title={formatMessage(JobMessages.systemHeading)}
                    valve5={props.data.valveStatus[4]}
                    valve6={props.data.valveStatus[5]}
                    valve2={props.data.valveStatus[1]}
                    timeTable={props.data.timeTable}
                    timeRange={props.timeRange}
                    showTimeDetails={perms.showTimestamps}
                    controllerOffRanges={controllerOffRanges}
                    logFileTimeRangeMin={logFileTimeRangeMin}
                    logFileTimeRangeMax={logFileTimeRangeMax}
                  />
                </IonCol>

                <IonCol size="12" sizeMd="6">
                  <PieHighPressure
                    title={formatMessage(JobMessages.highPressureHeading)}
                    valve3={props.data.valveStatus[2]}
                    valve16={props.data.valveStatus[15]}
                    timeTable={props.data.timeTable}
                    timeRange={props.timeRange}
                    showTimeDetails={perms.showTimestamps}
                    controllerOffRanges={controllerOffRanges}
                    logFileTimeRangeMin={logFileTimeRangeMin}
                    logFileTimeRangeMax={logFileTimeRangeMax}
                  />
                </IonCol>
              </IonRow>
            )}

            {perms.showIdleVsPressure && (
              <IonRow>
                <IonCol size="12">
                  <IdleVsHp
                    valve2={props.data.valveStatus[1]}
                    valve3={props.data.valveStatus[2]}
                    valve5={props.data.valveStatus[4]}
                    valve6={props.data.valveStatus[5]}
                    valve16={props.data.valveStatus[15]}
                    showTimeDetails={perms.showTimestamps}
                    controllerOffRanges={controllerOffRanges}
                    logFileTimeRangeMin={logFileTimeRangeMin}
                    logFileTimeRangeMax={logFileTimeRangeMax}
                  />
                </IonCol>
              </IonRow>
            )}

            {perms.showPressures && (
              <IonRow>
                <IonCol size="12">
                  <JobPressureCard
                    clampData={props.data.clamp}
                    inletData={props.data.inlet}
                    waterData={props.data.water}
                    timeTable={props.data.timeTable}
                    timeRangeMin={logFileTimeRangeMin}
                    timeRangeMax={logFileTimeRangeMax}
                    showTimestamps={perms.showTimestamps}
                    timezone={props.timezone}
                  />
                </IonCol>
              </IonRow>
            )}
          </IonGrid>
        )}
        {props.hasErrors && props.fetchProgress !== 1 && (
          <div className="ion-text-center">
            <br />
            <DqText variant="h2" color="dark">
              {formatMessage(JobMessages.errorFetchingLogDataTitle)}
            </DqText>
            <br />
            {!!props.isLimitedMode && (
              <div>
                <DqText variant="body-strong" color="medium">
                  {formatMessage(JobMessages.youMustGoOnlineDescription)}
                </DqText>
              </div>
            )}
          </div>
        )}
        {isMobile && jobInfoActions.length > 0 && (
          <>
            <IonFab vertical="bottom" horizontal="end" slot="fixed">
              <IonFabButton onClick={() => setShowActionSheet(true)} disabled={props.isLimitedMode}>
                <IonIcon icon={menuOutline} />
              </IonFabButton>
            </IonFab>
            <IonActionSheet
              isOpen={showActionSheet}
              onDidDismiss={() => setShowActionSheet(false)}
              buttons={jobInfoActions}
            />
          </>
        )}
        {!!handlePrint && (
          <JobDetailsPrintModal
            isOpen={printDialogOpen}
            onCancel={() => setPrintDialogOpen(false)}
            onConfirm={handlePrint}
          />
        )}
        {handleGuestShare && (
          <GuestShareModal
            isOpen={screenState.currentModal === 'GuestShare'}
            onClose={() => dispatch({ type: 'Modal:Closed', modal: 'GuestShare' })}
            onSubmit={handleGuestShare}
          />
        )}

        {handleFamilyOrgShare && (
          <FamilyOrgShareModal
            permissions={props.info?.permissions}
            isOpen={screenState.currentModal === 'FamilyOrgShare'}
            onClose={() => dispatch({ type: 'Modal:Closed', modal: 'FamilyOrgShare' })}
            onSubmit={handleFamilyOrgShare}
            orgFilter={screenState.orgFilter}
          />
        )}

        {handleDelete && (
          <YesNoModal
            isOpen={deleteWarningOpen}
            title={formatMessage(JobMessages.deleteLogTitle)}
            description={formatMessage(GenericMessages.deleteWarning)}
            yesButtonText={formatMessage(JobMessages.deleteLogFileButtonText)}
            yesButtonColor="danger"
            noButtonText={formatMessage(GenericMessages.cancelButtonLabel)}
            displayCloseButton={true}
            close={handleDelete}
          />
        )}
        <ShareTargetModal
          canShareJobReport={canShareJobReport}
          isOpen={screenState.currentModal === 'ShareTarget'}
          unexpiredPermissions={permissions}
          unexpiredEmailGuestShares={emailGuestShares}
          onSubmit={target => dispatch({ type: 'Share:TargetSelected', target })}
          onUnshare={handleUnshareReport}
          onClose={() => dispatch({ type: 'Modal:Closed', modal: 'ShareTarget' })}
        />

        <YesNoModal
          isOpen={!!handleComplete && screenState.currentModal === 'Complete'}
          title={formatMessage(JobMessages.completeJobDialogTitle)}
          description={formatMessage(JobMessages.completeJobDialogDescription)}
          yesButtonText={formatMessage(JobMessages.completeJobButtonLabel)}
          yesButtonColor="danger"
          noButtonText={formatMessage(GenericMessages.cancelButtonLabel)}
          displayCloseButton={true}
          close={handleComplete || stubYesNoClose}
        />
      </IonContent>
    </IonPage>
  );
};

interface HeaderProps {
  isMobile: boolean;
  title: string;
  headerButtons: JSX.Element | null;
  jobInfoProps: JobInfoProps;
  statusLabel: string;
  chipType: ChipType.JOB_COMPLETE | ChipType.JOB_INPROGRESS | ChipType.JOB_LIVE;
  jobInfoActions: JobInfoActionSheetButton[];
}

const Header: React.FC<HeaderProps> = props => {
  const { isMobile, title, headerButtons, jobInfoProps, statusLabel, chipType, jobInfoActions } = props;
  return isMobile ? (
    <MobileDetailPageHeader title={title} right={headerButtons} backButtonUrl={AppRouteConstants.LogsList} />
  ) : (
    <DesktopDetailPageHeader
      heading={jobInfoProps.description || title}
      subheading={jobInfoProps.subheading}
      chipProps={{
        title: statusLabel,
        type: chipType,
      }}
      left={true}
      right={
        <>
          {headerButtons}
          {jobInfoActions.map(action => {
            return (
              <>
                <IonButton key={action.key} onClick={action.handler} disabled={jobInfoProps.isLimitedMode}>
                  <IonIcon slot="icon-only" color="dark" size="large" icon={action.icon} title={action.text} />
                </IonButton>
                {action.badgeCount && (
                  <IonBadge class="action-button-badge" color="secondary">
                    {action.badgeCount}
                  </IonBadge>
                )}
              </>
            );
          })}
        </>
      }
    />
  );
};
